import moment from 'moment';
import { Optional } from 'utility-types';
import { HttpClient } from 'api/http-client';
import { Timestamp } from 'api/models';
import { TimesheetCalendarSourceType } from '..';
import { CalendarEventsParam, eventsUrl } from './utils';

export interface CalendarEvent {
  eventId: string;
  eventName: string;
  eventDescription: string;
  calendarId: string;
  startTime: Timestamp;
  endTime?: Timestamp;
  marked?: boolean;
  projectIds?: number[];
  timeEntryId: CalendarEvent['marked'] extends true ? number : undefined;
  sourceType: TimesheetCalendarSourceType;
  startDateTimeUTC: Timestamp;
  endDateTimeUTC: Timestamp;
  timeZone: string;
}
interface CalendarEventsResponse {
  events: CalendarEvent[];
}

export interface ComputedCalendarEvent
  extends Omit<CalendarEvent, 'projectIds' | 'endTime'> {
  duration: number;
  suggestedProjectIds?: number[];
  myProjectIds?: number[];
  endTime: Timestamp;
  sourceType: TimesheetCalendarSourceType;
}

export interface CalenderMeta {
  mergeStartEpoch: Timestamp;
  day: string;
  date: string;
}

export type CalendarEventWithMeta = [CalenderMeta, ComputedCalendarEvent[]];

export type CalendarEventsWithMeta = CalendarEventWithMeta[];

const getCalenderMeta = (event: CalendarEvent): CalenderMeta => ({
  mergeStartEpoch: moment(event.startTime).startOf('day').valueOf(),
  day: moment(event.startTime).format('ddd'),
  date: moment(event.startTime).format('DD MMM'),
});
const getNextDate = (date: Timestamp): Timestamp =>
  moment(date).add(1, 'day').valueOf();
const getDuration = ({
  startTime,
  endTime,
}: {
  startTime: Timestamp;
  endTime?: Timestamp;
}) =>
  moment(endTime || getNextDate(startTime)).diff(moment(startTime), 'minute'); // if backend doesn't give endDate its assumed as an full day event

const transformer = {
  toEventsWithMeta: (res: CalendarEventsResponse): CalendarEventsWithMeta => {
    const result: CalendarEventsWithMeta = [];

    res.events.forEach((event) => {
      const startTime = moment.utc(event.startDateTimeUTC).valueOf();
      const eventWithTimeStamp = {
        ...event,
        startTime,
        endTime: event.endDateTimeUTC
          ? moment.utc(event.endDateTimeUTC).valueOf()
          : getNextDate(startTime), // if backend doesn't give endDate its assumed as an full day event
      };
      const meta = getCalenderMeta(eventWithTimeStamp);
      const computedEvent: ComputedCalendarEvent = {
        ...eventWithTimeStamp,
        suggestedProjectIds: event.projectIds,
        duration: getDuration(eventWithTimeStamp),
      };
      const data = result.find(
        ([calenderMeta]) =>
          calenderMeta.mergeStartEpoch === meta.mergeStartEpoch,
      );

      if (data) {
        data[1].push(computedEvent);
      } else {
        result.push([meta, [computedEvent]]);
      }
    });

    const sortedResult = result.sort((a, b) => {
      return moment.utc(a[0].date).diff(moment.utc(b[0].date));
    });

    return sortedResult;
  },
};

class CalenderEventsHttpClient extends HttpClient {
  getCalenderEvents = (
    param: Optional<CalendarEventsParam>,
  ): Promise<CalendarEventsWithMeta> => {
    if (
      !param.calendarId ||
      !param.endDate ||
      !param.integrationId ||
      !param.startDate
    ) {
      return Promise.resolve([]);
    }

    return this.get<CalendarEventsResponse>(
      eventsUrl(param as CalendarEventsParam),
    ).then(transformer.toEventsWithMeta);
  };
}

export const CalenderEventApi = new CalenderEventsHttpClient();
